import React from 'react';
import { cn } from '@/lib/utils';
import type { CostCentre, TimesheetEntry } from '@/types/timesheet';
import type { OrganisationTimesheetSettings, OvertimeType } from '@/types/timesheetSettings';
import type { Project } from '@/types';
import { hasEditedHours } from '@/services/scheduledBookingService';
import type { StatusDotType } from '../../ScheduledBookings/StatusDot';
import type { DayName } from '../timesheetGridUtils';
import { RowSelectorCells } from './RowSelectorCells';
import { RowDescriptionCell } from './RowDescriptionCell';
import { RowHoursCells } from './RowHoursCells';
import { RowHoursToCompleteCell } from './RowHoursToCompleteCell';
import { RowTotalCell } from './RowTotalCell';
import { RowActionsCell } from './RowActionsCell';
import { useBudgetContext } from '@/hooks/useBudgetContext';
import { useFeatureFlags } from '@/contexts/FeatureFlagContext';

export interface TimesheetGridRowProps {
  entry: TimesheetEntry;
  index: number;
  /** Sum of the row's hours across the visible days. */
  rowTotal: number;
  allAvailableProjects: Project[];
  /** Globally loaded cost centres, used to enrich favourite/recent metadata. */
  costCentres: CostCentre[];
  /** Cost centres fetched per project code. */
  costCentresByProjectCode: Record<string, CostCentre[]>;
  visibleDays: readonly DayName[];
  currentWeekStart: Date;
  weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  nonWorkingDays: Set<string>;
  editingHours: Record<string, string | undefined>;
  openNoteCell: string | null;
  draftNote: string;
  isReadOnly: boolean;
  isDaily: boolean;
  isSearchingProjects: boolean;
  companyId: string | undefined;
  /** Employee the timesheet belongs to — used for budget context lookups. */
  employeeId: number | undefined;
  screenMode: 'live' | 'demo';
  overtimeEnabled: boolean;
  activeOvertimeTypes: OvertimeType[];
  organizationSettings: OrganisationTimesheetSettings | undefined;
  canDuplicate: boolean;
  canDelete: boolean;
  onUpdateEntry: (index: number, field: keyof TimesheetEntry, value: unknown) => void;
  onUpdateHours: (index: number, day: DayName, value: string) => void;
  onProjectSearch: (query: string) => void;
  onFetchCostCentres: (projectCode: string) => void;
  onCostCentreUsed: (costCentreId: string) => void;
  onSetEditingHours: (updater: (prev: Record<string, string | undefined>) => Record<string, string | undefined>) => void;
  onSetOpenNoteCell: (cellKey: string | null) => void;
  onSetDraftNote: (note: string) => void;
  onDuplicate: (index: number) => void;
  onDelete: (index: number) => void;
  onViewBookingDetails: (entry: TimesheetEntry) => void;
  onViewAbsenceDetails: (entry: TimesheetEntry) => void;
  onClassifyOvertime: (entry: TimesheetEntry) => void;
}

/** A single timesheet entry row: derives per-row state and composes the cells. */
function TimesheetGridRowComponent({
  entry,
  index,
  rowTotal,
  allAvailableProjects,
  costCentres,
  costCentresByProjectCode,
  visibleDays,
  currentWeekStart,
  weekStartDay,
  nonWorkingDays,
  editingHours,
  openNoteCell,
  draftNote,
  isReadOnly,
  isDaily,
  isSearchingProjects,
  companyId,
  employeeId,
  screenMode,
  overtimeEnabled,
  activeOvertimeTypes,
  organizationSettings,
  canDuplicate,
  canDelete,
  onUpdateEntry,
  onUpdateHours,
  onProjectSearch,
  onFetchCostCentres,
  onCostCentreUsed,
  onSetEditingHours,
  onSetOpenNoteCell,
  onSetDraftNote,
  onDuplicate,
  onDelete,
  onViewBookingDetails,
  onViewAbsenceDetails,
  onClassifyOvertime,
}: TimesheetGridRowProps) {
  const project = allAvailableProjects.find(p => p.id === entry.projectId);
  // Cost centres filtered by project code (if available).
  // In live mode these are fetched from the API filtered by project code;
  // in demo mode all cost centres are used (no filtering).
  const projectCode = project?.code || entry.projectCode;
  const projectCostCentres = projectCode ? (costCentresByProjectCode[projectCode] || []) : [];
  // Merge with enriched global cost centres for favourites/recently used
  const enrichedCostCentres = projectCostCentres.map(cc => {
    const enriched = costCentres.find(ecc => ecc.id === cc.id);
    return enriched || cc;
  });

  // Resolve cost centre name from the enriched list (or fall back to denormalized field).
  const selectedCostCentre = enrichedCostCentres.find(cc => cc.id === entry.costCentreId)
    || costCentres.find(cc => cc.id === entry.costCentreId);
  const costCentreName = selectedCostCentre?.name || entry.costCentreName;

  const { hoursToComplete } = useFeatureFlags();
  const showHoursToComplete = hoursToComplete && organizationSettings?.showHoursToComplete;

  // Budget context — only meaningful when a project is actually selected.
  const budgetContext = useBudgetContext({
    projectId: entry.projectId,
    employeeId,
    screenMode,
    projectCode,
    costCentreName,
    enabled: Boolean(showHoursToComplete && projectCode && entry.projectId),
  });

  const isScheduled = entry.isFromSchedule || false;
  const isDismissed = entry.isDismissed || false;
  const isEdited = isScheduled && !isDismissed && hasEditedHours(entry);
  const isAbsence = entry.sourceIntegration === 'people-hr';

  const statusType: StatusDotType = isDismissed
    ? 'dismissed'
    : isEdited
      ? 'edited'
      : 'scheduled';

  return (
    <tr
      className={cn(
        "border-b hover:bg-muted/50",
        // Absence entry styling (takes priority) - purple dotted left border
        isAbsence && "bg-purple-50/30 hover:bg-purple-50/50 border-l-4 border-dotted",
        // Scheduled entry styling (pale blue shade) - blue double left border
        !isAbsence && isScheduled && "bg-blue-50/40 hover:bg-blue-50/60 border-l-4 border-double",
        // Billable entry styling (green solid left accent) - only for non-scheduled, non-absence entries
        !isAbsence && !isScheduled && project?.billable && "border-l-4"
      )}
      style={{
        borderLeftColor: isAbsence
          ? 'var(--electricviolet-400)'
          : isScheduled
            ? 'var(--info-400)'
            : project?.billable
              ? 'var(--success-400)'
              : undefined
      }}
      aria-label={
        isAbsence
          ? `Absence entry: ${entry.description}, read-only`
          : project
            ? `${project.name}, ${project.billable ? 'billable' : 'non-billable'} project${isScheduled ? `, scheduled booking (${statusType})` : ', manual entry'}`
            : 'No project selected'
      }
    >
      <RowSelectorCells
        entry={entry}
        index={index}
        project={project}
        allAvailableProjects={allAvailableProjects}
        enrichedCostCentres={enrichedCostCentres}
        isReadOnly={isReadOnly}
        isAbsence={isAbsence}
        isScheduled={isScheduled}
        statusType={statusType}
        isSearchingProjects={isSearchingProjects}
        companyId={companyId}
        screenMode={screenMode}
        overtimeEnabled={overtimeEnabled}
        activeOvertimeTypes={activeOvertimeTypes}
        organizationSettings={organizationSettings}
        onUpdateEntry={onUpdateEntry}
        onProjectSearch={onProjectSearch}
        onFetchCostCentres={onFetchCostCentres}
        onCostCentreUsed={onCostCentreUsed}
      />
      {!isDaily && (
        <RowDescriptionCell
          entry={entry}
          index={index}
          isReadOnly={isReadOnly}
          isAbsence={isAbsence}
          organizationSettings={organizationSettings}
          onUpdateEntry={onUpdateEntry}
        />
      )}
      <RowHoursCells
        entry={entry}
        index={index}
        visibleDays={visibleDays}
        currentWeekStart={currentWeekStart}
        weekStartDay={weekStartDay}
        nonWorkingDays={nonWorkingDays}
        editingHours={editingHours}
        openNoteCell={openNoteCell}
        draftNote={draftNote}
        isReadOnly={isReadOnly}
        isAbsence={isAbsence}
        isDaily={isDaily}
        projectName={project?.name}
        organizationSettings={organizationSettings}
        onUpdateHours={onUpdateHours}
        onUpdateEntry={onUpdateEntry}
        onSetEditingHours={onSetEditingHours}
        onSetOpenNoteCell={onSetOpenNoteCell}
        onSetDraftNote={onSetDraftNote}
      />
      {showHoursToComplete && (
        <RowHoursToCompleteCell
          entry={entry}
          index={index}
          isReadOnly={isReadOnly || isAbsence}
          budgetContext={budgetContext}
          onUpdateEntry={onUpdateEntry}
        />
      )}
      <RowTotalCell total={rowTotal} />
      <RowActionsCell
        entry={entry}
        index={index}
        isReadOnly={isReadOnly}
        isScheduled={isScheduled}
        isAbsence={isAbsence}
        canDuplicate={canDuplicate}
        canDelete={canDelete}
        canClassifyOvertime={overtimeEnabled && activeOvertimeTypes.length > 0}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onViewBookingDetails={onViewBookingDetails}
        onViewAbsenceDetails={onViewAbsenceDetails}
        onClassifyOvertime={onClassifyOvertime}
      />
      <td aria-hidden="true" className="p-0" />
    </tr>
  );
}

/** Memoised so unrelated grid state changes do not re-render every row. */
export const TimesheetGridRow = React.memo(TimesheetGridRowComponent);
